import React, { useRef } from "react";
import type SvgIcon from "@mui/material/SvgIcon";
import { Tooltip } from "components/common/Tooltip";

export interface IconPickerOption<T> {
  value: T;
  /** The accessible name, and the tooltip unless `tooltip` says more. */
  label: string;
  Icon: typeof SvgIcon;
  tooltip?: string;
}

/** A row of icon-only choices with a thumb that slides to the picked one: the
 *  theme setting's look, for any small either/or. Arrow keys move the pick. */
export function IconPicker<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className = "",
  onHover,
}: {
  options: IconPickerOption<T>[];
  value: T;
  onChange: (next: T) => void;
  ariaLabel: string;
  className?: string;
  /** The option under the pointer, or `null` once it leaves the picker. */
  onHover?: (option: T | null) => void;
}) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const index = options.findIndex((o) => o.value === value);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (!step) return;
    e.preventDefault();
    const next = (index + step + options.length) % options.length;
    onChange(options[next].value);
    buttons.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`icon-picker ${className}`}
      style={{ "--icon-picker-index": index } as React.CSSProperties}
      onKeyDown={onKeyDown}
      onMouseLeave={() => onHover?.(null)}
    >
      <span className="icon-picker-thumb" aria-hidden />
      {options.map(({ value: option, label, Icon, tooltip }, i) => (
        <Tooltip key={option} title={tooltip ?? label} placement="bottom">
          <button
            ref={(el) => {
              buttons.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={option === value}
            aria-label={label}
            tabIndex={option === value ? 0 : -1}
            className="icon-picker-option"
            onMouseEnter={() => onHover?.(option)}
            onClick={() => option !== value && onChange(option)}
          >
            <Icon sx={{ fontSize: 14 }} />
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
